import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocationDetails } from 'app/shared/models/location-details';
import { FlexMonitorStatusResponse } from 'app/shared/models/flex-monitor-status';
import { StatusCodeService } from 'app/shared/services/status-code.service';
import type { FlexDiagnosticsDialogData } from '../flex-diagnostics-dialog.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    FLEX_PRESSURE_DEPTH_ENTITY,
    FLEX_TEMPERATURE_ENTITY,
    DEPTH_ENTITY,
    VELOCITY_ENTITY,
} from 'app/shared/constant';
import { AlarmDevice } from 'app/shared/components/location-card/store/configs/flex/models';
import { FlexMonitorStatusFacade } from './flex-monitor-status.facade';
import { FlexMonitorStatusApiService } from './flex-monitor-status-api.service';
import { FlexMonitorStatusDataSource } from './flex-monitor-status.data-source';
import {
    classifyFlexBattery,
    classifyFlexSignal,
    FLEX_BATTERY_GAUGE_CONFIGURATION,
    FLEX_SIGNAL_GAUGE_CONFIGURATION,
    formatFlexCoordinate,
    formatFlexDiagnosticTimestamp,
    formatFlexHydrographTimestamp,
    formatFlexStatusTimestamp,
    getFlexMp1Alarm,
    mapFlexConfiguredSensors,
    mapFlexDiagnosticTable,
} from './flex-monitor-status.mapper';
import {
    FlexConfiguredSensorView,
    FlexDiagnosticAngleView,
    FlexDiagnosticReadingRowView,
    FlexDiagnosticReadingView,
    FlexDiagnosticTableView,
    FlexGaugeState,
    FlexMonitorReading,
    FlexMonitorStatusState,
} from './flex-monitor-status.models';
import { parseLatestFlexReadings } from './hydrograph-compact-data.parser';

interface FlexReadingItem {
    id: number;
    labelKey: string;
}

type FlexCopyField = 'serialNumber' | 'physicalMonitorSerial' | 'imei' | 'iccid' | 'physicalSensorSerial';

const COPY_CONFIRMATION_DURATION_MS = 4000;

@Component({
    selector: 'app-flex-monitor-status',
    templateUrl: './flex-monitor-status.component.html',
    styleUrls: ['./flex-monitor-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        FlexMonitorStatusFacade,
        FlexMonitorStatusApiService,
        { provide: FlexMonitorStatusDataSource, useExisting: FlexMonitorStatusApiService },
    ],
})
export class FlexMonitorStatusComponent implements OnInit, OnDestroy {
    @Input() public dialogData: FlexDiagnosticsDialogData;
    @Output() public closeRequested = new EventEmitter<void>();

    public state: FlexMonitorStatusState | null = null;
    public configuredSensors: FlexConfiguredSensorView[] = [];
    public diagnosticTable: FlexDiagnosticTableView | null = null;
    public readings = new Map<number, FlexMonitorReading>();
    public signalState: FlexGaugeState = 'unknown';
    public batteryState: FlexGaugeState = 'unknown';
    public mp1Alarm: AlarmDevice | null = null;
    public isDarkTheme = false;
    public isPrinting = false;
    public copiedField: FlexCopyField | null = null;
    public copyMessage = '';
    public isWaitingForSensor = false;

    public readonly signalGaugeConfiguration = FLEX_SIGNAL_GAUGE_CONFIGURATION;
    public readonly batteryGaugeConfiguration = FLEX_BATTERY_GAUGE_CONFIGURATION;
    public readonly readingItems: FlexReadingItem[] = [
        { id: DEPTH_ENTITY, labelKey: 'DIAGNOSTICS.FLEX_MONITOR_STATUS.DEPTH' },
        { id: VELOCITY_ENTITY, labelKey: 'DIAGNOSTICS.FLEX_MONITOR_STATUS.VELOCITY' },
        { id: FLEX_PRESSURE_DEPTH_ENTITY, labelKey: 'DIAGNOSTICS.FLEX_MONITOR_STATUS.PRESSURE_DEPTH' },
        { id: FLEX_TEMPERATURE_ENTITY, labelKey: 'DIAGNOSTICS.FLEX_MONITOR_STATUS.TEMPERATURE' },
    ];

    private readonly destroy$ = new Subject<void>();
    private readonly printClass = 'flex-diagnostics-printing';
    private readonly darkThemeClass = 'blackTheme';
    private readonly afterPrintHandler = (): void => this.finishPrint();
    private copiedValue: string | null = null;
    private copyResetTimer: number | null = null;
    private isDestroyed = false;
    private restoreDarkThemeAfterPrint = false;
    private titleBeforePrint: string | null = null;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private facade: FlexMonitorStatusFacade,
        private translate: TranslateService,
        private statusCodeService: StatusCodeService,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.facade.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
            this.state = state;
            this.readings = parseLatestFlexReadings(state.hydrograph.data);
            this.configuredSensors = mapFlexConfiguredSensors(state.sensors.data, state.status.data);
            this.diagnosticTable = mapFlexDiagnosticTable(
                this.configuredSensors,
                state.diagnostics,
                state.diagnosticUnits.data,
            );
            this.signalState = classifyFlexSignal(state.status.data?.cellular?.signalStrengthDbm?.value);
            this.batteryState = classifyFlexBattery(state.status.data?.device?.batteryVoltage?.value);
            this.mp1Alarm = getFlexMp1Alarm(state.configuration.data);
            this.isWaitingForSensor =
                state.queueState === 'queueing' ||
                state.diagnostics.some((diagnostic) => diagnostic.state === 'running');
            this.cdr.markForCheck();
        });
        this.statusCodeService.userInfoThemeBS.pipe(takeUntil(this.destroy$)).subscribe((isDarkTheme) => {
            this.isDarkTheme = isDarkTheme;
            this.cdr.markForCheck();
        });
        window.addEventListener('afterprint', this.afterPrintHandler);
        this.facade.initialize(this.dialogData);
    }

    public ngOnDestroy(): void {
        this.isDestroyed = true;
        this.clearCopyResetTimer();
        window.removeEventListener('afterprint', this.afterPrintHandler);
        this.finishPrint();
        this.destroy$.next();
        this.destroy$.complete();
    }

    public refresh(): void {
        this.facade.refresh();
    }

    public print(): void {
        if (this.isPrinting) return;

        this.titleBeforePrint = this.document.title;
        this.document.title = `MonitorStatus_${this.location?.locationName || this.dialogData.locationName}`;
        this.restoreDarkThemeAfterPrint = this.document.body.classList.contains(this.darkThemeClass);
        if (this.restoreDarkThemeAfterPrint) this.document.body.classList.remove(this.darkThemeClass);
        this.isPrinting = true;
        this.document.body.classList.add(this.printClass);
        try {
            this.cdr.detectChanges();
            window.print();
        } catch (error: unknown) {
            this.finishPrint();
            throw error;
        }
    }

    public close(): void {
        this.closeRequested.emit();
    }

    public canCopy(value: string | null | undefined): boolean {
        return !!value?.trim();
    }

    public isCopied(field: FlexCopyField, value: string | null | undefined): boolean {
        return this.copiedField === field && this.copiedValue === value?.trim();
    }

    public copyActionText(field: FlexCopyField, value: string | null | undefined, labelKey: string): string {
        const label = this.translate.instant(labelKey);
        const messageKey = this.isCopied(field, value)
            ? 'DIAGNOSTICS.FLEX_MONITOR_STATUS.COPIED_VALUE'
            : 'DIAGNOSTICS.FLEX_MONITOR_STATUS.COPY_VALUE';
        return this.translate.instant(messageKey, { label });
    }

    public async copyValue(value: string | null | undefined, field: FlexCopyField, labelKey: string): Promise<void> {
        const normalizedValue = value?.trim();
        if (!normalizedValue) return;

        let copied = false;
        const clipboard = this.document.defaultView?.navigator.clipboard;
        if (clipboard) {
            try {
                await clipboard.writeText(normalizedValue);
                copied = true;
            } catch {
                copied = this.copyWithTextArea(normalizedValue);
            }
        } else {
            copied = this.copyWithTextArea(normalizedValue);
        }

        if (this.isDestroyed) return;

        const label = this.translate.instant(labelKey);
        this.clearCopyResetTimer();
        this.copiedField = copied ? field : null;
        this.copiedValue = copied ? normalizedValue : null;
        this.copyMessage = this.translate.instant(
            copied ? 'DIAGNOSTICS.FLEX_MONITOR_STATUS.COPIED_VALUE' : 'DIAGNOSTICS.FLEX_MONITOR_STATUS.COPY_FAILED',
            { label },
        );
        if (copied) {
            this.copyResetTimer = window.setTimeout(() => {
                this.copyResetTimer = null;
                if (this.copiedField !== field || this.copiedValue !== normalizedValue) return;

                this.copiedField = null;
                this.copiedValue = null;
                this.copyMessage = '';
                this.cdr.markForCheck();
            }, COPY_CONFIRMATION_DURATION_MS);
        }
        this.cdr.markForCheck();
    }

    public get status(): FlexMonitorStatusResponse | null {
        return this.state?.status.data ?? null;
    }

    public get location(): LocationDetails | null {
        return this.state?.location.data ?? null;
    }

    public get latitude(): number | null {
        return this.location?.coordinate?.latitude ?? this.location?.latitude ?? null;
    }

    public get longitude(): number | null {
        return this.location?.coordinate?.longitude ?? this.location?.longitude ?? null;
    }

    public get locationHeight(): number | null {
        return this.location?.height ?? this.location?.pipe?.pipe?.height ?? null;
    }

    public get locationWidth(): number | null {
        return this.location?.width ?? this.location?.pipe?.pipe?.width ?? null;
    }

    public get unavailableText(): string {
        return this.translate.instant('DIAGNOSTICS.FLEX_MONITOR_STATUS.UNAVAILABLE');
    }

    public get installationCaption(): string {
        const values: string[] = [];
        if (this.location?.installationType) values.push(this.location.installationType);
        if (this.location?.installationShape) values.push(this.location.installationShape);
        if (this.locationHeight !== null && this.locationWidth !== null) {
            values.push(`${this.locationHeight} × ${this.locationWidth} ${this.location?.depthUnit ?? ''}`.trim());
        }
        return values.join(' · ') || this.unavailableText;
    }

    public get installationDrawingAriaLabel(): string {
        return `${this.translate.instant('DIAGNOSTICS.FLEX_MONITOR_STATUS.DRAWING_ARIA')} ${this.installationCaption}`;
    }

    public reading(entityId: number): FlexMonitorReading | null {
        return this.readings.get(entityId) ?? null;
    }

    public formatReading(reading: FlexMonitorReading | null): string {
        if (!reading) return this.unavailableText;
        const precision = reading.precision ?? 3;
        return `${reading.value.toFixed(precision)}${reading.unit ? ` ${reading.unit}` : ''}`;
    }

    public formatReadingTimestamp(reading: FlexMonitorReading | null): string {
        if (!reading) return this.unavailableText;
        return formatFlexHydrographTimestamp(reading.timestampSeconds, this.state?.dateContext.data?.customer ?? null);
    }

    public formatStatusTimestamp(value: string | null | undefined): string {
        return formatFlexStatusTimestamp(value, this.state?.dateContext.data ?? null) || this.unavailableText;
    }

    public formatDiagnosticTimestamp(value: Date | null): string {
        return (
            formatFlexDiagnosticTimestamp(value, this.state?.dateContext.data?.customer ?? null) || this.unavailableText
        );
    }

    public formatDiagnosticReading(
        reading: FlexDiagnosticReadingView | null,
        row: FlexDiagnosticReadingRowView,
    ): string {
        if (!reading) return this.unavailableText;
        return `${reading.value.toFixed(row.precision)}${row.unit ? ` ${row.unit}` : ''}`;
    }

    public formatDiagnosticAngle(angle: FlexDiagnosticAngleView | null, precision: number): string {
        return angle ? `${angle.degrees.toFixed(precision)}°` : this.unavailableText;
    }

    public formatDiagnosticChange(change: number, precision: number): string {
        return `${change > 0 ? '+' : ''}${change.toFixed(precision)}`;
    }

    public formatCoordinate(value: number | null): string {
        return formatFlexCoordinate(value) || this.unavailableText;
    }

    public gaugeStateText(state: FlexGaugeState): string {
        return this.translate.instant(`DIAGNOSTICS.FLEX_MONITOR_STATUS.GAUGE.${state.toUpperCase()}`);
    }

    public hasResourceError(
        resource: keyof Pick<
            FlexMonitorStatusState,
            'status' | 'location' | 'configuration' | 'sensors' | 'hydrograph'
        >,
    ): boolean {
        return this.state?.[resource].state === 'error';
    }

    private clearCopyResetTimer(): void {
        if (this.copyResetTimer === null) return;

        window.clearTimeout(this.copyResetTimer);
        this.copyResetTimer = null;
    }

    private copyWithTextArea(value: string): boolean {
        const textArea = this.document.createElement('textarea');
        textArea.value = value;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        this.document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0, value.length);

        try {
            return this.document.execCommand('copy');
        } catch {
            return false;
        } finally {
            textArea.remove();
        }
    }

    private finishPrint(): void {
        if (this.titleBeforePrint !== null) {
            this.document.title = this.titleBeforePrint;
            this.titleBeforePrint = null;
        }
        this.document.body.classList.remove(this.printClass);
        if (this.restoreDarkThemeAfterPrint) this.document.body.classList.add(this.darkThemeClass);
        this.restoreDarkThemeAfterPrint = false;
        if (!this.isPrinting) return;

        this.isPrinting = false;
        if (!this.isDestroyed) this.cdr.detectChanges();
    }
}
